import React, { type JSX } from 'react';
import { FormField, SpaceBetween, Select } from '@amzn/awsui-components-core';
import {
  FeedbackStrategy,
  FeedbackFormState,
  FeedbackFormHandlers,
  FeedbackSubmissionContext,
} from './FeedbackStrategy';
import { PublicationIssueType } from '@amzn/awscat-pattern-library-service-model';
import { FeedbackRating, FeedbackRatingSystem } from '../../feedback-service';
import { getDefaultFilterSelection } from '../../../api/UtilsApi';
import RichTextDescriptionField from './RichTextDescriptionField';
import { getText } from '@amzn/awscat-editor';

const FeedbackTextLengthLimit = 1000;
const FeedbackKudos = getDefaultFilterSelection(PublicationIssueType.Kudos);

/**
 * Strategy for handling positive feedback (kudos/likes) on published documents.
 *
 * Intent:
 * Allows users to express appreciation and provide positive comments to authors.
 * This is a "like" action that records both a rating and optional commentary.
 *
 * Characteristics:
 * - Fixed feedback type (Kudos) - displayed but not editable
 * - Single "Comments" field for user input
 * - Dual submission: sends to both publication feedback and rating systems
 * - Simpler than document feedback (no issue type selection or action field)
 *
 * Validation:
 * - Requires non-empty comment text
 * - Enforces character limit (1000 chars)
 *
 * Submission:
 * - Sends publication feedback with Kudos issue type
 * - Records Like rating in feedback service for analytics
 * - Both submissions use the comment field content
 */
export class KudosFeedbackStrategy implements FeedbackStrategy {
  getHeaderText(): string {
    return 'Report feedback to author';
  }

  getSubjectLabel(): string {
    return 'Document title';
  }

  getDateLabel(): string {
    return 'Publish date';
  }

  renderFormFields(state: FeedbackFormState, handlers: FeedbackFormHandlers): JSX.Element {
    return (
      <SpaceBetween size="m">
        <FormField label="Type of feedback*" i18nStrings={{ errorIconAriaLabel: 'Error' }} stretch>
          <Select
            selectedOption={FeedbackKudos}
            onChange={() => {}}
            options={[FeedbackKudos]}
            selectedAriaLabel="Choose a type"
            disabled
            ariaRequired
          />
        </FormField>
        <RichTextDescriptionField
          value={state.comment}
          onChange={handlers.setComment}
          minLength={1}
          maxLength={FeedbackTextLengthLimit}
          onValidationChange={handlers.onDescriptionValidationChange}
          label="Comments*"
        />
      </SpaceBetween>
    );
  }

  validate(state: FeedbackFormState): boolean {
    const length = getText(state.comment).length;
    return length > 0 && length <= FeedbackTextLengthLimit;
  }

  async submit(state: FeedbackFormState, context: FeedbackSubmissionContext): Promise<void> {
    await context.mplService.sendPublicationFeedback(
      context.contentId,
      PublicationIssueType.Kudos,
      state.improvementFeedbackText,
      state.actionFeedbackText,
      state.comment,
    );
    await context.feedbackServiceClient.submitFeedback(
      context.contentId,
      context.userId,
      FeedbackRating.Like,
      FeedbackRatingSystem.LikeDislike,
      state.comment,
    );
  }
}
